import { useState, useCallback } from "react";
import {
  EmailIntakeQueueListEntity,
  PaginationInfo,
  EmailStatus,
  EmailCategory,
  GetEmailsUseCase,
  UpdateEmailStatusUseCase,
} from "../../domain";
import { EmailIntakeQueueRepositoryImpl } from "../../data/repositories/EmailIntakeQueueRepositoryImpl";

// Create repository instance (singleton)
const emailRepository = new EmailIntakeQueueRepositoryImpl();

// Create use cases
const getEmailsUseCase = new GetEmailsUseCase(emailRepository);
const updateStatusUseCase = new UpdateEmailStatusUseCase(emailRepository);

export interface UseEmailInboxReturn {
  emails: EmailIntakeQueueListEntity[];
  pagination: PaginationInfo | null;
  loading: boolean;
  error: string | null;
  setEmails: React.Dispatch<React.SetStateAction<EmailIntakeQueueListEntity[]>>;
  setPagination: React.Dispatch<React.SetStateAction<PaginationInfo | null>>;

  fetchEmails: (params?: {
    status?: EmailStatus;
    category?: EmailCategory;
    subdomain?: string;
    search?: string;
    reason?: string;
    limit?: number;
    offset?: number;
    sort_by?: "created_at" | "received_at" | "updated_at";
    sort_order?: "asc" | "desc";
  }) => Promise<void>;

  updateStatus: (ids: number[], status: "ignore" | "pushed") => Promise<void>;
  clearError: () => void;
}

export const useEmailInbox = (): UseEmailInboxReturn => {
  const [emails, setEmails] = useState<EmailIntakeQueueListEntity[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchEmails = useCallback(
    async (params?: {
      status?: EmailStatus;
      category?: EmailCategory;
      subdomain?: string;
      search?: string;
      reason?: string;
      limit?: number;
      offset?: number;
      sort_by?: "created_at" | "received_at" | "updated_at";
      sort_order?: "asc" | "desc";
    }) => {
      try {
        setLoading(true);
        setError(null);

        // Default to oldest first if sort_order not specified
        const result = await getEmailsUseCase.execute({
          sort_order: "asc",
          ...params,
        });

        setEmails(result.emails);
        setPagination(result.pagination);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to fetch emails";
        setError(errorMessage);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const updateStatus = useCallback(
    async (ids: number[], status: "ignore" | "pushed") => {
      try {
        // Don't set global loading - let the component handle specific loading states
        setError(null);
        await updateStatusUseCase.execute(ids, status);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : "Failed to update status";
        setError(errorMessage);
        throw err;
      }
    },
    []
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    emails,
    pagination,
    loading,
    error,
    setEmails,
    setPagination,
    fetchEmails,
    updateStatus,
    clearError,
  };
};
